// src/services/companyService.ts
import apiClient from '@/api/apiClient';
import { AxiosResponse } from 'axios';

// Types cho Company Service
export interface CompanyProfile {
    id: number;
    documentId: string;
    companyName: string;
    legalName?: string;
    businessRegistrationNumber?: string;
    taxId?: string;
    vatNumber?: string;
    companyType?: 'sole_proprietorship' | 'partnership' | 'limited_company' | 'corporation' | 'cooperative' | 'non_profit' | 'government' | 'other';
    industry?: any;
    businessDescription?: string;
    foundedYear?: number;
    numberOfEmployees?: string;
    companySize?: 'startup' | 'small' | 'medium' | 'large' | 'enterprise';
    logo?: any;
    coverImage?: any;
    headquarters?: any;
    branches?: any;
    contactInfo?: any;
    website?: string;
    socialLinks?: any;
    legalRepresentative?: any;
    authorizedPersons?: any;
    bankingInfo?: any;

    // ✅ Thêm các fields bị thiếu:
    businessLicense?: any;
    taxCertificate?: any;
    verificationDocuments?: any[];

    verificationLevel: 'unverified' | 'email_verified' | 'document_verified' | 'bank_verified' | 'fully_verified';
    companyStatus: 'active' | 'inactive' | 'suspended' | 'pending_verification' | 'under_review' | 'rejected';
    businessModel?: string;
    revenueRange?: string;
    creditRating?: string;
    riskLevel?: 'low' | 'medium' | 'high' | 'critical';
    complianceStatus?: any;
    certifications?: any;
    businessHours?: any;
    language: 'vi' | 'en' | 'zh' | 'ja' | 'ko';
    timezone?: string;
    currency?: 'VND' | 'USD' | 'EUR' | 'JPY' | 'CNY';
    profileCompleteness?: number;
    marketingConsent: boolean;
    dataRetentionConsent: boolean;
    customFields?: any;
    createdAt: string;
    updatedAt: string;
}

export interface CreateCompanyParams {
    companyName: string;
    legalName?: string;
    businessRegistrationNumber?: string;
    taxId?: string;
    vatNumber?: string;
    companyType?: string;
    industryId?: string;
    businessDescription?: string;
    foundedYear?: number;
    numberOfEmployees?: string;
    companySize?: string;
    headquarters?: any;
    contactInfo?: any;
    website?: string;
    socialLinks?: any;
    legalRepresentative?: any;
    businessModel?: string;
    language?: string;
    timezone?: string;
    currency?: string;
    marketingConsent?: boolean;
    dataRetentionConsent?: boolean;
}

export interface UpdateCompanyParams {
    companyName?: string;
    legalName?: string;
    businessRegistrationNumber?: string;
    taxId?: string;
    vatNumber?: string;
    companyType?: string;
    industryId?: string;
    businessDescription?: string;
    foundedYear?: number;
    numberOfEmployees?: string;
    companySize?: string;
    headquarters?: any;
    branches?: any;
    contactInfo?: any;
    website?: string;
    socialLinks?: any;
    legalRepresentative?: any;
    authorizedPersons?: any;
    businessModel?: string;
    revenueRange?: string;
    businessHours?: any;
    language?: string;
    timezone?: string;
    currency?: string;
    marketingConsent?: boolean;
    dataRetentionConsent?: boolean;
    customFields?: any;
}

export interface CompanySearchParams {
    search?: string;
    companyName?: string;
    taxId?: string;
    businessRegistrationNumber?: string;
    companyType?: string;
    industry?: string;
    companySize?: string;
    companyStatus?: string;
    verificationLevel?: string;
    location?: string;
    foundedYear?: number;
    revenueRange?: string;
    riskLevel?: string;
    page?: number;
    limit?: number;
    sort?: string;
}

export interface CompanyListResponse {
    data: CompanyProfile[];
    meta: {
        pagination: {
            page: number;
            pageSize: number;
            pageCount: number;
            total: number;
        };
    };
}

export interface CompanyEmployee {
    id: number;
    documentId: string;
    user: any;
    company: any;
    role: string;
    department?: string;
    position?: string;
    startDate: string;
    endDate?: string;
    isActive: boolean;
    permissions?: any;
    employeeStatus: 'active' | 'inactive' | 'suspended' | 'terminated';
}

export interface AddEmployeeParams {
    userId: string;
    role: string;
    department?: string;
    position?: string;
    startDate?: string;
    permissions?: any;
}

class CompanyService {
    /**
     * Tạo company profile mới
     */
    async createCompanyProfile(profileData: CreateCompanyParams): Promise<CompanyProfile> {
        try {
            const response: AxiosResponse<CompanyProfile> = await apiClient.post('/api/company-profiles', {
                data: profileData
            });
            return response.data;
        } catch (error) {
            console.error('Error creating company profile:', error);
            throw error;
        }
    }

    /**
     * Lấy company profile của user hiện tại
     */
    async getCurrentCompanyProfile(): Promise<CompanyProfile> {
        try {
            const response: AxiosResponse<CompanyProfile> = await apiClient.get('/api/company-profiles/me');
            return response.data;
        } catch (error) {
            console.error('Error fetching current company profile:', error);
            throw error;
        }
    }

    /**
     * Cập nhật company profile của user hiện tại
     * Strapi v5 sử dụng documentId cho UPDATE
     */
    async updateCurrentCompanyProfile(profileData: UpdateCompanyParams): Promise<CompanyProfile> {
        try {
            const response: AxiosResponse<CompanyProfile> = await apiClient.put('/api/company-profiles/me', {
                data: profileData
            });
            return response.data;
        } catch (error) {
            console.error('Error updating company profile:', error);
            throw error;
        }
    }

    /**
     * Lấy company profile theo ID (dành cho admin)
     */
    async getCompanyById(companyId: string): Promise<CompanyProfile> {
        try {
            const response: AxiosResponse<CompanyProfile> = await apiClient.get(`/api/company-profiles/${companyId}`, {
                params: {
                    populate: ['industry', 'user']
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching company by ID:', error);
            throw error;
        }
    }

    /**
     * Cập nhật company profile (dành cho admin)
     * Sử dụng documentId trong Strapi v5
     */
    async updateCompany(documentId: string, profileData: UpdateCompanyParams): Promise<CompanyProfile> {
        try {
            const response: AxiosResponse<CompanyProfile> = await apiClient.put(`/api/company-profiles/${documentId}`, {
                data: profileData
            });
            return response.data;
        } catch (error) {
            console.error('Error updating company:', error);
            throw error;
        }
    }

    /**
     * Cập nhật logo công ty
     */
    async updateCompanyLogo(file: File): Promise<any> {
        try {
            const formData = new FormData();
            formData.append('files', file);

            // Upload file trước
            const uploadResponse = await apiClient.post('/api/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            // Cập nhật logo trong company profile
            const logoId = uploadResponse.data[0].id;
            const response = await apiClient.put('/api/company-profiles/me', {
                data: { logo: logoId }
            });

            return response.data;
        } catch (error) {
            console.error('Error updating company logo:', error);
            throw error;
        }
    }

    /**
     * Cập nhật cover image công ty
     */
    async updateCompanyCoverImage(file: File): Promise<any> {
        try {
            const formData = new FormData();
            formData.append('files', file);

            // Upload file trước
            const uploadResponse = await apiClient.post('/api/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            // Cập nhật cover image trong company profile
            const coverImageId = uploadResponse.data[0].id;
            const response = await apiClient.put('/api/company-profiles/me', {
                data: { coverImage: coverImageId }
            });

            return response.data;
        } catch (error) {
            console.error('Error updating company cover image:', error);
            throw error;
        }
    }

    /**
     * Tìm kiếm và lọc companies (dành cho admin)
     */
    async searchCompanies(params: CompanySearchParams): Promise<CompanyListResponse> {
        try {
            const queryParams = new URLSearchParams();

            // Xử lý pagination
            if (params.page) queryParams.append('pagination[page]', params.page.toString());
            if (params.limit) queryParams.append('pagination[pageSize]', params.limit.toString());

            // Xử lý search
            if (params.search) {
                queryParams.append('filters[$or][0][companyName][$containsi]', params.search);
                queryParams.append('filters[$or][1][legalName][$containsi]', params.search);
                queryParams.append('filters[$or][2][taxId][$containsi]', params.search);
            }

            // Xử lý filters
            if (params.companyName) queryParams.append('filters[companyName][$containsi]', params.companyName);
            if (params.taxId) queryParams.append('filters[taxId][$containsi]', params.taxId);
            if (params.businessRegistrationNumber) queryParams.append('filters[businessRegistrationNumber][$containsi]', params.businessRegistrationNumber);
            if (params.companyType) queryParams.append('filters[companyType][$eq]', params.companyType);
            if (params.companySize) queryParams.append('filters[companySize][$eq]', params.companySize);
            if (params.companyStatus) queryParams.append('filters[companyStatus][$eq]', params.companyStatus);
            if (params.verificationLevel) queryParams.append('filters[verificationLevel][$eq]', params.verificationLevel);
            if (params.revenueRange) queryParams.append('filters[revenueRange][$eq]', params.revenueRange);
            if (params.riskLevel) queryParams.append('filters[riskLevel][$eq]', params.riskLevel);
            if (params.foundedYear) queryParams.append('filters[foundedYear][$eq]', params.foundedYear.toString());

            // Populate relations
            queryParams.append('populate[0]', 'industry');
            queryParams.append('populate[1]', 'user');

            // Sorting
            if (params.sort) queryParams.append('sort', params.sort);

            const response: AxiosResponse<CompanyListResponse> = await apiClient.get(`/api/company-profiles?${queryParams.toString()}`);
            return response.data;
        } catch (error) {
            console.error('Error searching companies:', error);
            throw error;
        }
    }

    /**
     * Thay đổi trạng thái công ty
     */
    async changeCompanyStatus(documentId: string, status: 'active' | 'inactive' | 'suspended' | 'under_review' | 'rejected'): Promise<any> {
        try {
            const response = await apiClient.put(`/api/company-profiles/${documentId}`, {
                data: { companyStatus: status }
            });
            return response.data;
        } catch (error) {
            console.error('Error changing company status:', error);
            throw error;
        }
    }

    /**
     * Upload business license
     */
    async uploadBusinessLicense(file: File): Promise<any> {
        try {
            const formData = new FormData();
            formData.append('files', file);

            // Upload file
            const uploadResponse = await apiClient.post('/api/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            // Update profile with business license
            const licenseId = uploadResponse.data[0].id;
            const response = await apiClient.put('/api/company-profiles/me', {
                data: { businessLicense: licenseId }
            });

            return response.data;
        } catch (error) {
            console.error('Error uploading business license:', error);
            throw error;
        }
    }

    /**
     * Upload tax certificate
     */
    async uploadTaxCertificate(file: File): Promise<any> {
        try {
            const formData = new FormData();
            formData.append('files', file);

            // Upload file
            const uploadResponse = await apiClient.post('/api/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            // Update profile with tax certificate
            const certificateId = uploadResponse.data[0].id;
            const response = await apiClient.put('/api/company-profiles/me', {
                data: { taxCertificate: certificateId }
            });

            return response.data;
        } catch (error) {
            console.error('Error uploading tax certificate:', error);
            throw error;
        }
    }

    /**
     * Upload verification documents
     */
    async uploadVerificationDocuments(files: File[]): Promise<any> {
        try {
            const formData = new FormData();
            files.forEach(file => {
                formData.append('files', file);
            });

            // Upload files
            const uploadResponse = await apiClient.post('/api/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            // Update profile with document IDs
            const documentIds = uploadResponse.data.map((file: any) => file.id);
            const response = await apiClient.put('/api/company-profiles/me', {
                data: { verificationDocuments: documentIds }
            });

            return response.data;
        } catch (error) {
            console.error('Error uploading verification documents:', error);
            throw error;
        }
    }

    /**
     * Verify company documents (admin function)
     */
    async verifyCompanyDocuments(documentId: string, status: 'approved' | 'rejected', notes?: string): Promise<any> {
        try {
            const verificationLevel = status === 'approved' ? 'document_verified' : 'unverified';
            const companyStatus = status === 'approved' ? 'active' : 'under_review';

            const response = await apiClient.put(`/api/company-profiles/${documentId}`, {
                data: {
                    verificationLevel,
                    companyStatus,
                    verificationNotes: notes
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error verifying company documents:', error);
            throw error;
        }
    }

    /**
     * Lấy danh sách nhân viên của công ty
     */
    async getCompanyEmployees(companyId: string): Promise<CompanyEmployee[]> {
        try {
            const response = await apiClient.get(`/api/company-employees`, {
                params: {
                    'filters[company][documentId][$eq]': companyId,
                    'populate[0]': 'user',
                    'populate[1]': 'company'
                }
            });
            return response.data.data;
        } catch (error) {
            console.error('Error fetching company employees:', error);
            throw error;
        }
    }

    /**
     * Thêm nhân viên vào công ty
     */
    async addEmployee(companyId: string, employeeData: AddEmployeeParams): Promise<CompanyEmployee> {
        try {
            const response = await apiClient.post('/api/company-employees', {
                data: {
                    ...employeeData,
                    company: companyId,
                    user: employeeData.userId,
                    employeeStatus: 'active',
                    isActive: true
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error adding employee:', error);
            throw error;
        }
    }

    /**
     * Cập nhật thông tin nhân viên
     */
    async updateEmployee(employeeId: string, employeeData: Partial<AddEmployeeParams>): Promise<CompanyEmployee> {
        try {
            const response = await apiClient.put(`/api/company-employees/${employeeId}`, {
                data: employeeData
            });
            return response.data;
        } catch (error) {
            console.error('Error updating employee:', error);
            throw error;
        }
    }

    /**
     * Xóa nhân viên khỏi công ty
     */
    async removeEmployee(employeeId: string): Promise<any> {
        try {
            // Soft delete: set inactive instead of hard delete
            const response = await apiClient.put(`/api/company-employees/${employeeId}`, {
                data: {
                    isActive: false,
                    employeeStatus: 'terminated',
                    endDate: new Date().toISOString()
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error removing employee:', error);
            throw error;
        }
    }

    /**
     * Lấy thống kê công ty
     */
    async getCompanyStatistics(timeRange?: string): Promise<any> {
        try {
            const params = timeRange ? { timeRange } : {};
            const response = await apiClient.get('/api/company-profiles/statistics', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching company statistics:', error);
            throw error;
        }
    }

    /**
     * Export danh sách companies (dành cho admin)
     */
    async exportCompanies(params: CompanySearchParams, format: 'csv' | 'excel' = 'csv'): Promise<Blob> {
        try {
            const queryParams = new URLSearchParams(params as any);
            queryParams.append('export', format);

            const response = await apiClient.get(`/api/company-profiles/export?${queryParams.toString()}`, {
                responseType: 'blob'
            });

            return response.data;
        } catch (error) {
            console.error('Error exporting companies:', error);
            throw error;
        }
    }

    /**
     * Bulk update companies (dành cho admin)
     */
    async bulkUpdateCompanies(companyIds: string[], updateData: any): Promise<any> {
        try {
            const response = await apiClient.post('/api/company-profiles/bulk-update', {
                companyIds,
                updateData
            });
            return response.data;
        } catch (error) {
            console.error('Error bulk updating companies:', error);
            throw error;
        }
    }

    /**
     * Xóa company profile (soft delete)
     */
    async deleteCompany(documentId: string): Promise<any> {
        try {
            const response = await apiClient.delete(`/api/company-profiles/${documentId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting company:', error);
            throw error;
        }
    }

    /**
     * Khôi phục company đã xóa
     */
    async restoreCompany(documentId: string): Promise<any> {
        try {
            const response = await apiClient.post(`/api/company-profiles/${documentId}/restore`);
            return response.data;
        } catch (error) {
            console.error('Error restoring company:', error);
            throw error;
        }
    }

    /**
     * Lấy danh sách industries để chọn
     */
    async getIndustries(): Promise<any[]> {
        try {
            const response = await apiClient.get('/api/industries', {
                params: {
                    'pagination[pageSize]': 100,
                    'sort': 'name'
                }
            });
            return response.data.data;
        } catch (error) {
            console.error('Error fetching industries:', error);
            throw error;
        }
    }

    /**
     * Validate business registration number
     */
    async validateBusinessRegistration(registrationNumber: string): Promise<{ isValid: boolean; companyInfo?: any }> {
        try {
            const response = await apiClient.post('/api/company-profiles/validate-registration', {
                registrationNumber
            });
            return response.data;
        } catch (error) {
            console.error('Error validating business registration:', error);
            throw error;
        }
    }

    /**
     * Validate tax ID
     */
    async validateTaxId(taxId: string): Promise<{ isValid: boolean; companyInfo?: any }> {
        try {
            const response = await apiClient.post('/api/company-profiles/validate-tax-id', {
                taxId
            });
            return response.data;
        } catch (error) {
            console.error('Error validating tax ID:', error);
            throw error;
        }
    }

    /**
     * Get company compliance report
     */
    async getComplianceReport(companyId: string): Promise<any> {
        try {
            const response = await apiClient.get(`/api/company-profiles/${companyId}/compliance-report`);
            return response.data;
        } catch (error) {
            console.error('Error fetching compliance report:', error);
            throw error;
        }
    }

    /**
     * Update company risk assessment
     */
    async updateRiskAssessment(companyId: string, riskData: any): Promise<any> {
        try {
            const response = await apiClient.put(`/api/company-profiles/${companyId}/risk-assessment`, {
                data: riskData
            });
            return response.data;
        } catch (error) {
            console.error('Error updating risk assessment:', error);
            throw error;
        }
    }
}

export default new CompanyService();